import { converObjectErrorInString, convertObjectToCamelCase } from "@/helpers"
import { useMutation, useQuery } from '@tanstack/react-query'

import { HistoryWithPagesProps, OrderManagmentPurchaseOrderItemsResponse, SupplyOrderCommentsProps, SupplyOrderCommentsWithPagesProps } from "@/types"
import { QUERIES_ADMIN } from "./api.constants"
import { api } from "./api.service"
import { BFF_SUPPLY_ORDER_MANAGMENT_PURCHASE_ORDER_COMMENTS_URL, BFF_SUPPLY_ORDER_MANAGMENT_PURCHASE_ORDER_HISTORY_URL, BFF_SUPPLY_ORDER_MANAGMENT_PURCHASE_ORDER_ITEMS_URL } from "./api.urls"

export const useOrderManagmentPurchaseOrderItems = (
  uuid: string,
  isEnabled = true,
) => {
  const getOrderManagmentPurchaseOrderItems = async (): Promise<{
    success: boolean
    message: string
    data?: OrderManagmentPurchaseOrderItemsResponse[]
  }> => {
    try {


      const { data } = await api.get(BFF_SUPPLY_ORDER_MANAGMENT_PURCHASE_ORDER_ITEMS_URL(uuid))

      return {
        success: true,
        message: 'Items fetched successfully!',
        data: convertObjectToCamelCase(data) as OrderManagmentPurchaseOrderItemsResponse[],
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SUPPLIER_ORDER_MANAGMENT_PURCHASE_ORDER_ITEMS.module, uuid],
    queryFn: () => getOrderManagmentPurchaseOrderItems(),
    enabled: isEnabled,
  })
}

export const useOrderManagmentPurchaseOrderCommentsList = (
  uuid?: string,
  params?: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderManagmentPurchaseOrderCommentsList = async (): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderCommentsWithPagesProps
  }> => {
    try {
      const formattedParams = Object.keys(params || {}).reduce((acc, key) => {
        if (params?.[key] && params?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: params?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_SUPPLY_ORDER_MANAGMENT_PURCHASE_ORDER_COMMENTS_URL(`${uuid}`), {
        params: {
          ...(formattedParams as Record<
            string,
            string
          >),
        },
      })

      return {
        success: true,
        message: 'Supply Order comments fetched successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderCommentsWithPagesProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SUPPLIER_ORDER_MANAGMENT_PURCHASE_ORDER_COMMENTS.module],
    queryFn: () => getOrderManagmentPurchaseOrderCommentsList(),
    enabled: isEnabled,
  })
}

export const useOrderManagmentPurchaseOrderCommentCreate = (uuid?: string) => {
  const purchaseOrderCommentCreate = async (payload: {
    type?: string
    subject?: string
    comment: string
  }): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderCommentsProps
  }> => {
    try {
      const { data } = await api.post(
        BFF_SUPPLY_ORDER_MANAGMENT_PURCHASE_ORDER_COMMENTS_URL(`${uuid}`),
        payload,
      )

      return {
        success: true,
        message: 'Successfully!',
        data,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLIER_ORDER_MANAGMENT_PURCHASE_ORDER_COMMENTS.module],
    mutationFn: (payload: {
      type?: string
      subject?: string
      comment: string
    }) => purchaseOrderCommentCreate(payload),
  })
}

export const useOrderManagmentPurchaseOrderCommentEdit = (supplyOrderUuid: string) => {
  const purchaseOrderCommentEdit = async (payload: {
    uuid?: string
    comment: string
  }): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderCommentsProps
  }> => {
    try {
      const { data } = await api.patch(
        `${BFF_SUPPLY_ORDER_MANAGMENT_PURCHASE_ORDER_COMMENTS_URL(`${supplyOrderUuid}`)}${payload?.uuid}/`,
        payload,
      )

      return {
        success: true,
        message: 'Successfully!',
        data,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLIER_ORDER_MANAGMENT_PURCHASE_ORDER_COMMENTS.module],
    mutationFn: (payload: { uuid?: string; comment: string }) =>
      purchaseOrderCommentEdit(payload),
  })
}

export const useOrderManagmentPurchaseOrderCommentRemove = (supplyOrderUuid: string) => {
  const purchaseOrderCommentRemove = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderCommentsProps
  }> => {
    try {
      const { data } = await api.delete(
        `${BFF_SUPPLY_ORDER_MANAGMENT_PURCHASE_ORDER_COMMENTS_URL(`${supplyOrderUuid}`)}${uuid}/`,
      )

      return {
        success: true,
        message: 'Supply order comment deleted successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderCommentsProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLIER_ORDER_MANAGMENT_PURCHASE_ORDER_COMMENTS.module],
    mutationFn: (uuid: string) => purchaseOrderCommentRemove(uuid),
  })
}

export const usePurchaseOrderHistoryList = (
  uuid?: string,
  params?: Record<string, string | number>,
) => {
  const getPurchaseOrderHistoryList = async (): Promise<{
    success: boolean
    message: string
    data?: HistoryWithPagesProps
  }> => {
    try {
      const formattedParams = Object.keys(params || {}).reduce((acc, key) => {
        if (params?.[key] && params?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: params?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(
        BFF_SUPPLY_ORDER_MANAGMENT_PURCHASE_ORDER_HISTORY_URL(`${uuid}`),
        {
          params: {
            ...(formattedParams as Record<
              string,
              string
            >),
          },
        },
      )

      return {
        success: true,
        message: 'Supply Order histories fetched successfully!',
        data: convertObjectToCamelCase(data) as HistoryWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get supply order histories!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SUPPLIER_ORDER_MANAGMENT_PURCHASE_ORDER_HISTORY.module],
    queryFn: () => getPurchaseOrderHistoryList(),
    enabled: !!uuid,
  })
}